/**
 * A backend hívása. Egyetlen hely, ahol `fetch` szerepel.
 *
 * A kérés-kontextus SZERVEROLDALI — a felület nem küld bérlőt, és a cég-
 * szűrőt sem ő dönti el. Amit küld, az a felületi CÉGVÁLASZTÁS (#73,
 * `X-Company-Id`): azt a szerver a hatókörrel METSZI, tehát szűkíteni tud
 * vele, tágítani nem. A munkamenetet a böngésző viszi, `httpOnly` sütiben (#14); hogy
 * mit lát, azt a szerver dönti el a saját kikényszerítési pontján (2.6/23).
 * Ha a szűrő a felületről jönne, a szűkítés a kliensen múlna, és az nem
 * szűkítés.
 */

import { env } from '@/config/env';
import { getSelectedCompany, setSelectedCompany } from './company-selection';

/**
 * A CSRF-token sütijének neve és a fejléc, amiben visszaküldjük. A szerver
 * `session-cookie.ts`-e ugyanezt a két nevet használja — ha elválnak, MINDEN
 * írás 403-mal jön vissza.
 */
const CSRF_COOKIE = 'fakt.csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/** Amihez nem kell CSRF-token — ugyanaz a halmaz, mint a szerver őrében. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** A cégválasztás fejléce — a szerver `COMPANY_SELECTION_HEADER`-je (#73). */
const COMPANY_HEADER = 'X-Company-Id';

/**
 * A szerver 403-ának kódja, ha a TÁROLT választás avult el (a céget közben
 * elvették). Erre a válasz a választás törlése, nem a „nincs jogod" hiba.
 */
export const COMPANY_SELECTION_INVALID = 'COMPANY_SELECTION_INVALID';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * MELYIK MEZŐRŐL SZÓL A HIBA — ha a szerver megmondta (#42).
     *
     * ⚠️ NEM DÍSZ. A mezőjavítás `400`-a a mező nevével jön
     * (`document.controller.ts`), és a felület ebből tudja, melyik sort
     * jelölje meg. Enélkül az egész űrlap pirosodna, és a könyvelőnek kellene
     * kitalálnia, hol nézzen.
     */
    readonly fieldKey?: string,
    /** A szerver gépi hibakódja, ha adott (`COMPANY_SELECTION_INVALID`). */
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A CSRF-TOKEN KIOLVASÁSA A SÜTIBŐL (#14).
 *
 * A munkamenet `httpOnly` sütiben utazik — azt a JS NEM éri el, és nem is
 * kell neki. A CSRF-token sütije viszont SZÁNDÉKOSAN olvasható: a szerver
 * ebből várja vissza a `X-CSRF-Token` fejlécet, és a kettő egyezéséből
 * tudja, hogy a kérést a MI lapunk indította.
 *
 * ⚠️ EZ NEM AZ AUTENTIKÁCIÓ. Ha valaki ezt a tokent ellopja, semmire nem
 * megy vele: a munkamenetet a másik, olvashatatlan süti hordozza.
 */
function csrfToken(): string | null {
  // A `document.cookie` egyetlen sztring, `a=1; b=2` alakban. Az érték
  // tartalmazhat `=`-t (base64url nem, de a HMAC-elválasztó pont igen),
  // ezért CSAK az első egyenlőségjelnél vágunk.
  for (const part of document.cookie.split('; ')) {
    const at = part.indexOf('=');
    if (at > 0 && part.slice(0, at) === CSRF_COOKIE) {
      return decodeURIComponent(part.slice(at + 1));
    }
  }
  return null;
}

/**
 * A kérés kísérő fejlécei.
 *
 * ⚠️ A `credentials: 'include'` NEM ELHAGYHATÓ, még azonos originen sem —
 * a `fetch` alapértelmezése `same-origin`, ami itt ugyan elég lenne, de a
 * `VITE_API_URL`-lel másik originre állítva NÉMÁN elhagyná a sütit, és
 * minden kérés 401 lenne. Kiírva a szándék egyértelmű.
 */
function withSession(init: RequestInit): RequestInit {
  // ⚠️ A CSRF-TOKEN CSAK AZ ÍRÁSOKHOZ KELL, és csak azokhoz olvassuk ki.
  // A szerver őre ugyanezt a határt húzza (`csrf.guard.ts`): a GET/HEAD/
  // OPTIONS definíció szerint nem változtat semmit. Olvasásnál a sütihez
  // hozzányúlni nemcsak fölösleges, hanem ÁRT is — a `document.cookie`
  // getter valódi `URL` konstruktort kíván, és egy olyan hívási helyen,
  // ahol azt kicserélték (objektum-URL-ek), az egész kérés elszállna.
  const method = (init.method ?? 'GET').toUpperCase();
  const token = SAFE_METHODS.has(method) ? null : csrfToken();
  const company = getSelectedCompany();

  return {
    ...init,
    credentials: 'include',
    headers: {
      ...(token === null ? {} : { [CSRF_HEADER]: token }),
      ...(company === null ? {} : { [COMPANY_HEADER]: company }),
      ...init.headers,
    },
  };
}

// A házirend szerint közvetlen `import.meta.env` nincs — a config/env dob,
// ha egy kötelező változó hiányzik.
const BASE_URL = env.apiUrl;

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  // A választás, AMIVEL a kérés elmegy — a hibaágon ehhez mérünk (lent).
  const sentCompany = getSelectedCompany();
  const response = await fetch(
    `${BASE_URL}${path}`,
    withSession({ ...init, headers: { 'Content-Type': 'application/json', ...init.headers } })
  );

  if (!response.ok) {
    // A szerver üzenete magyarul jön; a felület azt mutatja, nem egy sajátot
    // — különben két helyen kellene ugyanazt karbantartani.
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      fieldKey?: string;
      code?: string;
    } | null;
    // AZ ELAVULT VÁLASZTÁS ITT TÖRLŐDIK, egy helyen. A cégválasztó erre
    // „az összes cég"-re áll vissza, és a felület újra kér — a hívó hibát
    // kap, de a következő kérés már a teljes hatókörrel megy.
    //
    // ⚠️ CSAK HA MÉG UGYANAZ A VÁLASZTÁS. Egy késve érkező 403 az ELŐZŐ
    // választásról szól — ha közben a könyvelő már másik céget választott,
    // annak törlése az ő friss döntését írná felül.
    if (
      response.status === 403 &&
      body?.code === COMPANY_SELECTION_INVALID &&
      getSelectedCompany() === sentCompany
    ) {
      setSelectedCompany(null);
    }
    throw new ApiError(response.status, body?.message ?? '', body?.fieldKey, body?.code);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * EGY FÁJL BÁJTJAI, az ALÁÍRT címéről (`/documents/:id/file-url`).
 *
 * NEM az `api()`: a cím már TELJES útvonal (a szerver adja, előtaggal
 * együtt), és a válasz nem JSON. A munkamenet-sütit viszont ugyanúgy visszük
 * — a cím aláírt, de ártani nem árt, és egy jövőbeli szigorítás nem törné el.
 */
export async function fetchFile(signedUrl: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(signedUrl, withSession({ signal }));
  if (!response.ok) {
    throw new ApiError(response.status, '');
  }
  return response.blob();
}

/** Amit a `/auth/me` ad vissza — a kontextus felületi tükre. */
export interface Me {
  actor: {
    kind: 'user' | 'support';
    role?: 'ADMIN' | 'ACCOUNTANT' | 'CLIENT';
    name: string;
    locale: string;
    companyId?: string | null;
  };
  companies:
    | { kind: 'tenant' }
    | { kind: 'assigned'; companyIds: string[] }
    | { kind: 'single'; companyId: string };
}

/**
 * A belépés válasza. ⚠️ TOKEN NINCS BENNE (#14) — a munkamenet `httpOnly`
 * sütiben érkezik, amit a JS nem lát. Ha ide valaha visszakerülne egy
 * `accessToken` mező, az az egész átállást visszavonná.
 */
export interface LoginResponse {
  user: { id: string; name: string; email: string; role: string; locale: string };
}

/**
 * Amit a varázslink megnyitása mond — `POST /auth/magic-link/check`.
 *
 * A `valid: false` NÉGY OKOT fed le egyszerre (lejárt, elhasznált, ismeretlen,
 * vagy már van jelszó), és ez a szerver döntése: a megkülönböztetés csak
 * annak mondana valamit, aki a tokent találgatja. A felületnek mind a négy
 * ugyanaz a mondat — `auth.linkExpired`. (Ugyanez a típus a PWA-ban is áll;
 * a két szerviz nem oszt kódot, a szerződés a backendé.)
 */
export interface MagicLinkCheck {
  valid: boolean;
  name: string | null;
}
